/**
 * Renders the current and draft Meta config for plan review on the Campaign Manager tab.
 * Meta configs nest ad sets, each with an ad and its creative, and store money in minor
 * units and times as ISO strings, which the tab's generic key-by-key rendering would show
 * raw. These sections give every Meta campaign, budget, ad set, ad and creative value a
 * translated label and the tab's current-versus-draft diff row, so a reviewer can check a
 * Meta draft before publishing it.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import { ConfigField } from 'src/components/campaigns/ConfigField';
import { formatUtcDateTimeWithZone } from 'src/utils/formatDateTime';
import {
  type MetaAdConfig,
  type MetaAdSetConfig,
  type MetaBudgetConfig,
  type MetaCampaignBudgetConfig,
  type MetaCreativeAutomationConfig,
  type MetaCreativeConfig,
  type MetaPlacementConfig,
  type MetaPublisherCampaignConfig,
  type MetaTargetingConfig,
} from 'src/store/generated/leela-api-generated';

const CURRENCY_LOCALE = 'en-US';
const LIST_SEPARATOR = ', ';
const META_MAX_AGE = 65;
const META_GENDER_LABEL_KEYS: Readonly<Record<number, string>> = {
  1: 'publisher_campaign_config_meta_gender_male',
  2: 'publisher_campaign_config_meta_gender_female',
};

interface FieldRow<T> {
  readonly labelKey: string;
  readonly format: (value: T, t: TranslateFn) => string;
}

interface DiffPair<T> {
  readonly current?: T;
  readonly draft?: T;
}

function formatList (values: ReadonlyArray<string | number> | undefined): string {
  return values?.map(String).join(LIST_SEPARATOR) ?? '';
}

/** Meta amounts are integers in the currency's minor unit, so the currency's own precision converts them (JPY has none). */
function formatMinorUnits (amountMinorUnits: number, currency: string, t: TranslateFn): string {
  try {
    const formatter = new Intl.NumberFormat(CURRENCY_LOCALE, { currency, style: 'currency' });
    return formatter.format(amountMinorUnits / 10 ** formatter.resolvedOptions().maximumFractionDigits);
  } catch {
    // An unknown currency has no known precision, so show the stored amount rather than guess.
    return t('publisher_campaign_config_meta_amount_minor_units', { amount: amountMinorUnits, currency });
  }
}

function formatAgeRange ({ ageMin, ageMax }: MetaTargetingConfig): string {
  const max = ageMax >= META_MAX_AGE ? `${META_MAX_AGE}+` : String(ageMax);
  return `${ageMin}–${max}`;
}

function formatGenders ({ genders }: MetaTargetingConfig, t: TranslateFn): string {
  if (!genders?.length) return t('publisher_campaign_config_meta_gender_all');
  return genders
    .map(gender => gender in META_GENDER_LABEL_KEYS ? t(META_GENDER_LABEL_KEYS[gender]) : String(gender))
    .join(LIST_SEPARATOR);
}

function formatSwitch (isOn: boolean, t: TranslateFn): string {
  return t(isOn ? 'publisher_campaign_config_meta_value_on' : 'publisher_campaign_config_meta_value_off');
}

// A Meta draft stores null for a field the agent cleared, so campaign fields are read as possibly null.
function formatOptionalSwitch (isOn: boolean | null | undefined, t: TranslateFn): string {
  return typeof isOn === 'boolean' ? formatSwitch(isOn, t) : '';
}

function formatTime (value: string | null | undefined): string {
  return value ? formatUtcDateTimeWithZone(value, value) : '';
}

// The spend cap carries no currency of its own; it is in the ad account currency, which the budget also carries.
function formatSpendCap (config: MetaPublisherCampaignConfig, accountCurrency: string | undefined, t: TranslateFn): string {
  const currency = config.budget?.currency ?? accountCurrency;
  if (typeof config.spendCapMinorUnits !== 'number') return '';
  return currency
    ? formatMinorUnits(config.spendCapMinorUnits, currency, t)
    : t('publisher_campaign_config_meta_amount_minor_units_no_currency', { amount: config.spendCapMinorUnits });
}

/**
 * Pairs published items with their draft counterparts by identity, falling back to
 * position when either side has no identity (a new draft ad set has no id until approval).
 * Published order is kept; draft-only items follow as additions.
 */
function pairByIdentity<T> (
  current: readonly T[],
  draft: readonly T[],
  getIdentity: (item: T) => string | undefined,
): Array<DiffPair<T>> {
  const draftIndexByCurrentIndex = new Map<number, number>();
  const pairedDraftIndexes = new Set<number>();
  const recordPair = (currentIndex: number, draftIndex: number): void => {
    draftIndexByCurrentIndex.set(currentIndex, draftIndex);
    pairedDraftIndexes.add(draftIndex);
  };

  current.forEach((item, currentIndex) => {
    const identity = getIdentity(item);
    if (!identity) return;
    const draftIndex = draft.findIndex(
      (candidate, index) => !pairedDraftIndexes.has(index) && getIdentity(candidate) === identity,
    );
    if (draftIndex >= 0) recordPair(currentIndex, draftIndex);
  });
  current.forEach((item, index) => {
    if (draftIndexByCurrentIndex.has(index) || index >= draft.length || pairedDraftIndexes.has(index)) return;
    if (!getIdentity(item) || !getIdentity(draft[index])) recordPair(index, index);
  });

  const pairs: Array<DiffPair<T>> = current.map((item, index) => {
    const draftIndex = draftIndexByCurrentIndex.get(index);
    return { current: item, draft: draftIndex === undefined ? undefined : draft[draftIndex] };
  });
  draft.forEach((item, index) => {
    if (!pairedDraftIndexes.has(index)) pairs.push({ draft: item });
  });
  return pairs;
}

function createCampaignRows (accountCurrency: string | undefined): ReadonlyArray<FieldRow<MetaPublisherCampaignConfig>> {
  return [
    { labelKey: 'publisher_campaign_config_meta_campaign_name', format: config => config.name ?? '' },
    { labelKey: 'publisher_campaign_config_meta_objective', format: config => config.objective ?? '' },
    { labelKey: 'publisher_campaign_config_meta_bid_strategy', format: config => config.bidStrategy ?? '' },
    {
      labelKey: 'publisher_campaign_config_meta_budget_sharing',
      format: (config, t) => formatOptionalSwitch(config.adsetBudgetSharingEnabled, t),
    },
    { labelKey: 'publisher_campaign_config_meta_spend_cap', format: (config, t) => formatSpendCap(config, accountCurrency, t) },
    { labelKey: 'publisher_campaign_config_meta_start_time', format: config => formatTime(config.startTime) },
    { labelKey: 'publisher_campaign_config_meta_end_time', format: config => formatTime(config.endTime) },
  ];
}

const BUDGET_AMOUNT_ROW: FieldRow<MetaBudgetConfig> = {
  labelKey: 'publisher_campaign_config_meta_budget_amount',
  format: (budget, t) => formatMinorUnits(budget.amountMinorUnits, budget.currency, t),
};
const BUDGET_PERIOD_ROW: FieldRow<MetaBudgetConfig> = {
  labelKey: 'publisher_campaign_config_meta_budget_period',
  format: budget => budget.period,
};

const CAMPAIGN_BUDGET_ROWS: ReadonlyArray<FieldRow<MetaCampaignBudgetConfig>> = [
  BUDGET_AMOUNT_ROW,
  { labelKey: 'publisher_campaign_config_meta_budget_level', format: budget => budget.level },
  BUDGET_PERIOD_ROW,
];

const AD_SET_BUDGET_ROWS: ReadonlyArray<FieldRow<MetaBudgetConfig>> = [BUDGET_AMOUNT_ROW, BUDGET_PERIOD_ROW];

const AD_SET_ROWS: ReadonlyArray<FieldRow<MetaAdSetConfig>> = [
  { labelKey: 'publisher_campaign_config_meta_ad_set_name', format: adSet => adSet.name },
  { labelKey: 'publisher_campaign_config_meta_ad_set_product_strategy', format: adSet => adSet.productStrategyId },
  { labelKey: 'publisher_campaign_config_meta_ad_set_page', format: adSet => adSet.pageId },
  { labelKey: 'publisher_campaign_config_meta_ad_set_instagram_user', format: adSet => adSet.instagramUserId ?? '' },
  { labelKey: 'publisher_campaign_config_meta_ad_set_kind', format: adSet => adSet.kind },
  { labelKey: 'publisher_campaign_config_meta_ad_set_optimization_goal', format: adSet => adSet.optimizationGoal },
  { labelKey: 'publisher_campaign_config_meta_ad_set_billing_event', format: adSet => adSet.billingEvent },
  { labelKey: 'publisher_campaign_config_meta_ad_set_locales', format: adSet => formatList(adSet.locales) },
  { labelKey: 'publisher_campaign_config_meta_ad_set_dsa_beneficiary', format: adSet => adSet.dsaBeneficiary ?? '' },
  { labelKey: 'publisher_campaign_config_meta_ad_set_dsa_payor', format: adSet => adSet.dsaPayor ?? '' },
  { labelKey: 'publisher_campaign_config_meta_ad_set_brand_safety', format: adSet => formatList(adSet.brandSafetyContentFilterLevels) },
];

const PLACEMENT_ROWS: ReadonlyArray<FieldRow<MetaPlacementConfig>> = [
  { labelKey: 'publisher_campaign_config_meta_placements_platforms', format: placements => formatList(placements.publisherPlatforms) },
  { labelKey: 'publisher_campaign_config_meta_placements_facebook_positions', format: placements => formatList(placements.facebookPositions) },
  { labelKey: 'publisher_campaign_config_meta_placements_instagram_positions', format: placements => formatList(placements.instagramPositions) },
  { labelKey: 'publisher_campaign_config_meta_placements_devices', format: placements => formatList(placements.devicePlatforms) },
];

const TARGETING_ROWS: ReadonlyArray<FieldRow<MetaTargetingConfig>> = [
  { labelKey: 'publisher_campaign_config_meta_targeting_countries', format: targeting => formatList(targeting.countries) },
  { labelKey: 'publisher_campaign_config_meta_targeting_age_range', format: formatAgeRange },
  { labelKey: 'publisher_campaign_config_meta_targeting_genders', format: formatGenders },
  {
    labelKey: 'publisher_campaign_config_meta_targeting_interests',
    format: targeting => formatList(targeting.interests?.map(interest => interest.name)),
  },
  {
    labelKey: 'publisher_campaign_config_meta_targeting_advantage_audience',
    format: (targeting, t) => formatSwitch(targeting.advantageAudience, t),
  },
];

const AD_ROWS: ReadonlyArray<FieldRow<MetaAdConfig>> = [
  { labelKey: 'publisher_campaign_config_meta_ad_name', format: ad => ad.name },
  { labelKey: 'publisher_campaign_config_meta_ad_format', format: ad => ad.format },
];

const CREATIVE_ROWS: ReadonlyArray<FieldRow<MetaCreativeConfig>> = [
  { labelKey: 'publisher_campaign_config_meta_creative_link', format: creative => creative.linkUrl },
  { labelKey: 'publisher_campaign_config_meta_creative_caption', format: creative => creative.caption ?? '' },
  { labelKey: 'publisher_campaign_config_meta_creative_message', format: creative => creative.messageTemplate },
  { labelKey: 'publisher_campaign_config_meta_creative_headline', format: creative => creative.nameTemplate },
  { labelKey: 'publisher_campaign_config_meta_creative_description', format: creative => creative.descriptionTemplate ?? '' },
  { labelKey: 'publisher_campaign_config_meta_creative_call_to_action', format: creative => creative.callToActionType },
];

const AUTOMATION_ROWS: ReadonlyArray<FieldRow<MetaCreativeAutomationConfig>> = [
  { labelKey: 'publisher_campaign_config_meta_automation_adapt_to_placement', format: automation => automation.adaptToPlacement },
  { labelKey: 'publisher_campaign_config_meta_automation_advantage_plus_creative', format: automation => automation.advantagePlusCreative },
  { labelKey: 'publisher_campaign_config_meta_automation_product_video', format: automation => automation.productVideo },
];

interface DiffRowsProps<T> {
  readonly current: T | undefined;
  readonly draft: T | undefined;
  readonly heading?: string;
  readonly rows: ReadonlyArray<FieldRow<T>>;
  readonly showDraft: boolean;
  readonly testId?: string;
}

function DiffRows<T> ({ current, draft, heading, rows, showDraft, testId }: DiffRowsProps<T>): React.ReactElement | null {
  const { t } = useTranslation();
  const formatSide = (row: FieldRow<T>, value: T | undefined): string => value ? row.format(value, t) : '';
  const fields = rows.flatMap(row => {
    const currentValue = formatSide(row, current);
    // With a draft shown, a value the draft lacks is a removal on publish, so it formats as ''.
    const draftValue = showDraft ? formatSide(row, draft) : undefined;
    if (!currentValue && !draftValue) return [];
    return [
      <ConfigField current={currentValue} draft={draftValue} key={row.labelKey} label={t(row.labelKey)} showDraft={showDraft} />,
    ];
  });
  if (fields.length === 0) return null;
  return (
    <Column data-testid={testId} spacing="200">
      {heading && <Text type="h100">{heading}</Text>}
      {fields}
    </Column>
  );
}

interface CardTitleProps {
  readonly isNested: boolean;
  readonly isRemoved: boolean;
  readonly title: string;
}

const CardTitle: React.FC<CardTitleProps> = ({ isNested, isRemoved, title }) => {
  const { t } = useTranslation();
  const text = isRemoved ? <span style={{ textDecoration: 'line-through' }}>{title}</span> : title;
  return (
    <Row alignmentVertical="center" spacing="200">
      {isNested ? <Text tag="strong" type="b300">{text}</Text> : <Text type="h100">{text}</Text>}
      {isRemoved && <Text color="secondary" type="b200">{t('publisher_campaign_config_not_in_draft')}</Text>}
    </Row>
  );
};

interface MetaAdCardProps {
  readonly pair: DiffPair<MetaAdConfig>;
  readonly position: number;
  readonly showDraft: boolean;
  readonly testId: string;
}

const MetaAdCard: React.FC<MetaAdCardProps> = ({ pair, position, showDraft, testId }) => {
  const { t } = useTranslation();
  const title = pair.current?.name ??
    pair.draft?.name ??
    t('publisher_campaign_config_meta_ad_fallback_name', { number: position + 1 });
  return (
    <Box data-testid={testId} spacingInset="400" type="outline">
      <Column spacing="300">
        <CardTitle isNested isRemoved={showDraft && pair.draft === undefined} title={title} />
        <DiffRows current={pair.current} draft={pair.draft} rows={AD_ROWS} showDraft={showDraft} />
        <DiffRows
          current={pair.current?.creative}
          draft={pair.draft?.creative}
          heading={t('publisher_campaign_config_meta_creative_heading')}
          rows={CREATIVE_ROWS}
          showDraft={showDraft}
        />
        <DiffRows
          current={pair.current?.creative.automation}
          draft={pair.draft?.creative.automation}
          heading={t('publisher_campaign_config_meta_automation_heading')}
          rows={AUTOMATION_ROWS}
          showDraft={showDraft}
        />
      </Column>
    </Box>
  );
};

interface AdSetCardProps {
  readonly pair: DiffPair<MetaAdSetConfig>;
  readonly position: number;
  readonly showDraft: boolean;
}

const AdSetCard: React.FC<AdSetCardProps> = ({ pair, position, showDraft }) => {
  const { t } = useTranslation();
  const testId = `meta-ad-set-card-${position}`;
  const title = pair.current?.name ??
    pair.draft?.name ??
    t('publisher_campaign_config_meta_ad_set_fallback_name', { number: position + 1 });
  const currentAds = pair.current?.ads ?? [];
  const draftAds = showDraft ? pair.draft?.ads ?? [] : [];
  const adPairs = pairByIdentity(currentAds, draftAds, ad => ad.key);
  return (
    <Box data-testid={testId} spacingInset="400" type="fill">
      <Column spacing="300">
        <CardTitle isNested={false} isRemoved={showDraft && pair.draft === undefined} title={title} />
        <DiffRows current={pair.current} draft={pair.draft} rows={AD_SET_ROWS} showDraft={showDraft} />
        <DiffRows
          current={pair.current?.placements}
          draft={pair.draft?.placements}
          heading={t('publisher_campaign_config_meta_placements_heading')}
          rows={PLACEMENT_ROWS}
          showDraft={showDraft}
        />
        <DiffRows
          current={pair.current?.targeting}
          draft={pair.draft?.targeting}
          heading={t('publisher_campaign_config_meta_targeting_heading')}
          rows={TARGETING_ROWS}
          showDraft={showDraft}
        />
        <DiffRows
          current={pair.current?.budget}
          draft={pair.draft?.budget}
          heading={t('publisher_campaign_config_meta_ad_set_budget_heading')}
          rows={AD_SET_BUDGET_ROWS}
          showDraft={showDraft}
        />
        {adPairs.length > 0 && (
          <>
            <Text type="h100">
              {t('publisher_campaign_config_meta_ads_heading', { count: showDraft ? draftAds.length : currentAds.length })}
            </Text>
            {adPairs.map((adPair, adPosition) => (
              <MetaAdCard
                key={adPosition}
                pair={adPair}
                position={adPosition}
                showDraft={showDraft}
                testId={`${testId}-ad-${adPosition}`}
              />
            ))}
          </>
        )}
      </Column>
    </Box>
  );
};

export interface MetaPlanSectionsProps {
  readonly accountCurrency?: string;
  readonly current: MetaPublisherCampaignConfig | undefined;
  readonly draft: MetaPublisherCampaignConfig | undefined;
  readonly showDraft: boolean;
}

export const MetaPlanSections: React.FC<MetaPlanSectionsProps> = ({ accountCurrency, current, draft, showDraft }) => {
  const { t } = useTranslation();
  const shownDraft = showDraft ? draft : undefined;
  const currentAdSets = current?.adSets ?? [];
  const draftAdSets = shownDraft?.adSets ?? [];
  const adSetPairs = pairByIdentity(currentAdSets, draftAdSets, adSet => adSet.adSetConfigId);
  const hasBudget = Boolean(current?.budget ?? shownDraft?.budget);
  return (
    <Column data-testid="meta-plan-sections" spacing="400">
      <DiffRows
        current={current}
        draft={shownDraft}
        rows={createCampaignRows(accountCurrency)}
        showDraft={showDraft}
        testId="meta-plan-campaign"
      />
      {hasBudget && (
        <Column data-testid="meta-plan-budget" spacing="400">
          <Text type="h200">{t('publisher_campaign_config_meta_budget_heading')}</Text>
          <Divider />
          <DiffRows current={current?.budget} draft={shownDraft?.budget} rows={CAMPAIGN_BUDGET_ROWS} showDraft={showDraft} />
        </Column>
      )}
      {adSetPairs.length > 0 && (
        <Column data-testid="meta-plan-ad-sets" spacing="400">
          <Text type="h200">
            {t('publisher_campaign_config_meta_ad_sets_heading', { count: showDraft ? draftAdSets.length : currentAdSets.length })}
          </Text>
          <Divider />
          {adSetPairs.map((pair, position) => (
            <AdSetCard key={position} pair={pair} position={position} showDraft={showDraft} />
          ))}
        </Column>
      )}
    </Column>
  );
};
